import { ExtendedArticleEntityType } from "./types";
import { JobStatusEnum } from "@/lib/types/job";
import {
	JobItemEntityTypeInsert,
	JobWithShare,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { JobStoreUnion, useJobStore } from ".";
import { v4 as uuidv4 } from "uuid";
import { Region, TpValue } from "../../types/enums";
import { Logger } from "@/lib/logger/Logger";
import {
	JobDocumentTypeEnum,
	getDeliveryNoteTitle,
} from "../../pages/job-page/job-document/job-document.types";
import {
	JobStage,
	JobListEntryWithStages,
	JobListEntryFlat,
} from "./job-list.store";
import { TariffWithPricesViewType } from "../Catalogs";
import { roundToCents } from "../../lib/utils/calculate";
import type { FieldProblem } from "./field-problems.types";
import { validatePaymentSlipDebtor } from "../../pdf-templates/pdf-template-components/qr-payment-slip/payment-slip-parties";

export const invCoveredByOptions = [
	{
		value: "private",
		label: "Privat",
	},
	{
		value: "kvg",
		label: "KVG-Tarif",
	},
	{
		value: "uvg",
		label: "UVG-Tarif",
	},
	{
		value: "social_welfare_office",
		label: "Sozialtarife",
	},
	{
		value: "other",
		label: "Andere",
	},
];

export enum RoundToType {
	TO_INTEGER = 0,
	TO_FIRST_DECIMAL = 1,
	TO_SECOND_DECIMAL = 2,
	TO_THIRD_DECIMAL = 3,
	TO_RAPPEN = 4,
}

const getTpValue = (tariffOwnTp: number | null, jobTpValue: number) => {
	return tariffOwnTp ? tariffOwnTp : jobTpValue;
};

export const calculateTariffPrice = (
	tariff: TariffWithPricesViewType,
	jobTpVariation: number | null,
	jobPriceListId: string
): number => {
	if (!tariff.price_items) {
		return 0;
	}
	const priceListItem = tariff.price_items.find(
		(item) => item.price_list_id === jobPriceListId
	);

	let price = priceListItem?.price;
	if (!price) {
		return 0;
	}

	// It's important to re-calculate if jobTpVariation is null if the
	// since the user might change it from a number back to no jobTpVariation
	if (jobTpVariation !== undefined && jobTpVariation !== 0) {
		price = price + (price * (jobTpVariation ?? 0)) / 100;
	}
	return roundToCents(price);
};

export const tariffToJobItem = (
	tariff: TariffWithPricesViewType,
	jobDocumentId: number,
	jobPriceListId: string,
	jobTpValue: TpValue,
	jobTpVariation: number
):
	| {
			error: null;
			data: JobItemEntityTypeInsert;
	  }
	| {
			error: string;
			data: null;
	  } => {
	if (!tariff.code_e) {
		return { error: "Tariff code_e is required", data: null };
	}
	if (!tariff.id) {
		return { error: "Tariff id is required", data: null };
	}

	const price = calculateTariffPrice(tariff, jobTpVariation, jobPriceListId);
	const tpValue = getTpValue(tariff.own_tp, jobTpValue);
	return {
		error: null,
		data: {
			id: uuidv4(),
			additional_text: tariff.additional_text,
			ce_number: undefined,
			cluster: undefined,
			code: tariff.code,
			code_e: tariff.code_e,
			composition: tariff.composition,
			description: tariff.description_de,
			fpo_id: undefined, // migration field
			is_print_additional_text: undefined,
			is_req_dr_tax: tariff.is_req_dr_tax,
			is_warranty: undefined,
			job_document_id: jobDocumentId,
			lot: undefined, // articles field
			manufacturer: undefined,
			note: undefined,
			unit: undefined, // articles field
			price: price,
			profile_id: undefined,
			profile2_id: undefined,
			profile3_id: undefined,
			techn_perc_2: undefined,
			techn_perc_3: undefined,
			quantity: tariff.standard_quantity ?? 1,

			tp_value: tpValue,
			type: JobItemTypeEnum.TARIFF,

			legacy_tax_rate: undefined, // legacy
			legacy_tariff_id: undefined, // legacy
			legacy_article_id: undefined, // legacy
		},
	};
};

export const formatArticle = (
	item: ExtendedArticleEntityType,
	jobDocumentId: number
):
	| {
			data: JobItemEntityTypeInsert;
			error: null;
	  }
	| {
			error: string;
			data: null;
	  } => {
	Logger.info(`[formatArticle] item: ${JSON.stringify(item)}`);

	// Filter out properties that are not needed in the job item
	const {
		id,
		code_e, // for articles we only have code_e because there's no need for an official code
		description_de,
		description_fr,
		description_it,
		created_at,
		modified_at,
		is_new,
		standard_quantity,
		custom,
		description_other,
		margin,
		modified_stock_quantity_at,
		organization_id,
		purchase_price,
		purchase_quantity,
		stock_quantity,
		unit,
		art_id,
		tax_rate,
		...filteredItem
	} = item;

	const newRow: JobItemEntityTypeInsert = {
		...filteredItem,
		id: uuidv4(),
		quantity: standard_quantity ?? 1,
		description: description_de, // TODO: localize
		code: code_e,
		code_e: code_e,
		job_document_id: jobDocumentId,
		// snapshot so the unit survives article deletion and one-time items
		unit: unit,
		price: item.price ?? 0,
		tp_value: 0, // Articles do not have a TP value
		type: item.type ?? JobItemTypeEnum.ARTICLE_MATERIAL,

		legacy_tax_rate: undefined, // legacy
		legacy_article_id: undefined, // legacy
	};

	return {
		error: null,
		data: newRow,
	};
};

export const adjustJobStatus = (
	state: JobStoreUnion,
	jobId: number,
	targetStatus: JobStatusEnum,
	currentStatus: JobStatusEnum
) => {
	let jobIndex = -1;
	let stageIndex: JobStage | null = null;

	if (state.jobList[currentStatus].stages) {
		// Search through all stages to find the job
		for (const stage of Object.keys(
			(state.jobList[currentStatus] as JobListEntryWithStages).data
		)) {
			const stageData = (
				state.jobList[currentStatus] as JobListEntryWithStages
			).data[stage as JobStage];
			const foundJobIndex = stageData.jobs.findIndex(
				(j: JobWithShare) => j.id === jobId
			);

			if (foundJobIndex !== -1) {
				stageIndex = stage as JobStage;
				jobIndex = foundJobIndex;
				break;
			}
		}
	} else {
		jobIndex = (
			state.jobList[currentStatus] as JobListEntryFlat
		).data.jobs.findIndex((j: JobWithShare) => j.id === jobId);
	}

	// Always sync the currently-viewed job, even if it's absent from the list
	// buckets (which is the case when the user navigates straight to a job
	// without first loading the corresponding overview filter).
	if (state.job?.id === jobId) {
		state.job.status = targetStatus;
	}

	if (jobIndex === -1) return;

	const job = state.jobList[currentStatus].stages
		? (state.jobList[currentStatus] as JobListEntryWithStages).data[
				stageIndex as JobStage
			].jobs[jobIndex]
		: (state.jobList[currentStatus] as JobListEntryFlat).data.jobs[
				jobIndex
			];

	const stage = getJobStage(job);

	// Remove the job from the list of its current status
	if (state.jobList[currentStatus].stages) {
		(state.jobList[currentStatus] as JobListEntryWithStages).data[
			stageIndex as JobStage
		].jobCount -= 1;
	} else {
		(state.jobList[currentStatus] as JobListEntryFlat).data.jobCount -= 1;
	}

	if (state.jobList[currentStatus].stages) {
		(state.jobList[currentStatus] as JobListEntryWithStages).data[
			stageIndex as JobStage
		].jobs.splice(jobIndex, 1);
	} else {
		(state.jobList[currentStatus] as JobListEntryFlat).data.jobs.splice(
			jobIndex,
			1
		);
	}

	// Add the job to the list of its new status
	if (state.jobList[targetStatus].stages) {
		(state.jobList[targetStatus] as JobListEntryWithStages).data[
			stage
		].jobCount += 1;
		(state.jobList[targetStatus] as JobListEntryWithStages).data[
			stage
		].jobs.unshift(job);
	} else {
		(state.jobList[targetStatus] as JobListEntryFlat).data.jobCount += 1;
		(state.jobList[targetStatus] as JobListEntryFlat).data.jobs.unshift(
			job
		);
	}
};

export const documentDrivesJobStatus = (type: string | null) =>
	type === JobDocumentTypeEnum.DeliveryNote ||
	type === JobDocumentTypeEnum.CreditNote;

export const findJobListStatus = (
	state: Pick<JobStoreUnion, "jobList">,
	jobId: number
): JobStatusEnum | null => {
	for (const [status, entry] of Object.entries(state.jobList)) {
		const buckets = entry.stages ? Object.values(entry.data) : [entry.data];
		if (buckets.some((bucket) => bucket.jobs.some((j) => j.id === jobId))) {
			return Number(status) as JobStatusEnum;
		}
	}
	return null;
};

export const adjustJobStage = (
	state: JobStoreUnion,
	jobId: number,
	newDocumentTypes: JobDocumentTypeEnum[]
) => {
	const status = state.job?.status as JobStatusEnum;
	if (!status || !state.jobList[status]?.stages) return;

	const listEntry = state.jobList[status] as JobListEntryWithStages;

	let oldStage: JobStage | null = null;
	let jobIndex = -1;

	for (const stage of Object.keys(listEntry.data) as JobStage[]) {
		const idx = listEntry.data[stage].jobs.findIndex(
			(j: JobWithShare) => j.id === jobId
		);
		if (idx !== -1) {
			oldStage = stage;
			jobIndex = idx;
			break;
		}
	}

	if (oldStage === null || jobIndex === -1) return;

	const job = listEntry.data[oldStage].jobs[jobIndex];
	job.job_document_types = newDocumentTypes;

	const newStage = getJobStage(job);
	if (newStage === oldStage) return;

	listEntry.data[oldStage].jobs.splice(jobIndex, 1);
	listEntry.data[oldStage].jobCount -= 1;

	listEntry.data[newStage].jobs.unshift(job);
	listEntry.data[newStage].jobCount += 1;
};

export const jobDocumentTypeNamePresets = (
	type: JobDocumentTypeEnum,
	region: Region | null = null
) => {
	let name = "Neues Dokument";
	if (type === JobDocumentTypeEnum.DeliveryNote) {
		name = getDeliveryNoteTitle(region);
	} else if (type === JobDocumentTypeEnum.Quotation) {
		name = "Kostenvoranschlag";
	} else if (type === JobDocumentTypeEnum.CreditNote) {
		name = "Gutschrift";
	} else if (type === JobDocumentTypeEnum.Tracing) {
		name = "Rückverfolgung";
	}
	return name;
};

export const isUpdateJobCodeAllowed = (jobStatus: JobStatusEnum) => {
	if (
		jobStatus === JobStatusEnum.COMPLETED ||
		jobStatus === JobStatusEnum.BOOKED_MONTHLY ||
		jobStatus === JobStatusEnum.BOOKED_SINGLE
	) {
		return {
			isAllowed: false,
			explanation: "Der Auftrag ist abgeschlossen oder gebucht",
		};
	}

	return {
		isAllowed: true,
		needsConfirmation: false,
		explanation: "Der Auftrag ist nicht abgeschlossen oder gebucht",
	};
};

export const getJobListEntry = (jobs: JobWithShare[], stage: JobStage) => {
	const categories = [
		JobDocumentTypeEnum.Quotation,
		JobDocumentTypeEnum.DeliveryNote,
		JobDocumentTypeEnum.CreditNote,
	];

	if (!jobs) return { jobCount: 0, jobs: [] };

	switch (stage) {
		case "empty":
			const emptyJobs = jobs.filter(
				(j) =>
					!j.job_document_types ||
					!j.job_document_types.some((t) =>
						categories.includes(t as JobDocumentTypeEnum)
					)
			);
			return { jobCount: emptyJobs.length, jobs: emptyJobs };
		case JobDocumentTypeEnum.Quotation:
			const quotationJobs = jobs.filter(
				(j) =>
					j.job_document_types &&
					j.job_document_types.includes(
						JobDocumentTypeEnum.Quotation
					) &&
					!j.job_document_types.includes(
						JobDocumentTypeEnum.DeliveryNote
					) &&
					!j.job_document_types.includes(
						JobDocumentTypeEnum.CreditNote
					)
			);
			return { jobCount: quotationJobs.length, jobs: quotationJobs };
		case JobDocumentTypeEnum.DeliveryNote:
			const deliveryNoteJobs = jobs.filter(
				(j) =>
					j.job_document_types &&
					j.job_document_types.includes(
						JobDocumentTypeEnum.DeliveryNote
					) &&
					!j.job_document_types.includes(
						JobDocumentTypeEnum.CreditNote
					)
			);
			return {
				jobCount: deliveryNoteJobs.length,
				jobs: deliveryNoteJobs,
			};
		case JobDocumentTypeEnum.CreditNote:
			const creditNoteJobs = jobs.filter(
				(j) =>
					j.job_document_types &&
					j.job_document_types.includes(
						JobDocumentTypeEnum.CreditNote
					)
			);
			return { jobCount: creditNoteJobs.length, jobs: creditNoteJobs };
		default:
			return { jobCount: jobs.length, jobs: jobs };
	}
};

export const getJobStage = (job: JobWithShare) => {
	if (
		!job.job_document_types ||
		(!job.job_document_types.includes(JobDocumentTypeEnum.Quotation) &&
			!job.job_document_types.includes(
				JobDocumentTypeEnum.DeliveryNote
			) &&
			!job.job_document_types.includes(JobDocumentTypeEnum.CreditNote))
	)
		return "empty";
	if (
		job.job_document_types.includes(JobDocumentTypeEnum.Quotation) &&
		!job.job_document_types.includes(JobDocumentTypeEnum.DeliveryNote) &&
		!job.job_document_types.includes(JobDocumentTypeEnum.CreditNote)
	)
		return JobDocumentTypeEnum.Quotation;
	if (
		job.job_document_types.includes(JobDocumentTypeEnum.DeliveryNote) &&
		!job.job_document_types.includes(JobDocumentTypeEnum.CreditNote)
	)
		return JobDocumentTypeEnum.DeliveryNote;
	if (job.job_document_types.includes(JobDocumentTypeEnum.CreditNote))
		return JobDocumentTypeEnum.CreditNote;
	return "empty";
};

export const getStatusAndStageFromId = (jobId: number) => {
	const jobList = useJobStore.getState().jobList;
	let foundStatus: JobStatusEnum | null = null;
	let foundStage: JobStage | null = null;

	for (const status of Object.keys(jobList) as unknown as JobStatusEnum[]) {
		if (jobList[status].stages) {
			for (const stage of Object.keys(
				(jobList[status] as JobListEntryWithStages).data
			) as unknown as JobStage[]) {
				const jobs = (jobList[status] as JobListEntryWithStages).data[
					stage
				].jobs;
				const jobIndex = jobs.findIndex(
					(j: JobWithShare) => j.id === jobId
				);
				if (jobIndex !== -1) {
					foundStatus = status as JobStatusEnum;
					foundStage = stage as JobStage;
					break;
				}
			}
		} else {
			const jobs = (jobList[status] as JobListEntryFlat).data.jobs;
			const jobIndex = jobs.findIndex(
				(j: JobWithShare) => j.id === jobId
			);
			if (jobIndex !== -1) {
				foundStatus = status as JobStatusEnum;
				foundStage = null;
				break;
			}
		}
	}

	return { status: foundStatus ?? null, stage: foundStage ?? null };
};

export const NO_PATIENT_PROBLEM: FieldProblem = {
	fieldId: "patient_select",
	message: "Bitte wählen Sie einen Patienten aus.",
};

/** Field problems that keep a job from pricing tariffs: missing TP-Wert and/or Preisliste. */
export const getJobPricingProblems = (
	job: Pick<JobWithShare, "tp_value" | "price_list_id">
): FieldProblem[] => [
	...(job.tp_value
		? []
		: [{ fieldId: "jobs.tp_value", message: "Der Taxpunktwert fehlt." }]),
	...(job.price_list_id
		? []
		: [
				{
					fieldId: "jobs.price_list_id",
					message: "Die Preisliste fehlt.",
				},
			]),
];

export const jobPricingProblemsMessage = (problems: FieldProblem[]): string => {
	if (problems.length > 1)
		return "Dem Auftrag fehlen Taxpunktwert und Preisliste.";
	return problems[0]?.fieldId === "jobs.tp_value"
		? "Dem Auftrag fehlt der Taxpunktwert."
		: "Dem Auftrag fehlt die Preisliste.";
};

/**
 * Patient fields that keep an invoice to the patient from being issued: the QR slip's
 * debtor rules plus a complete address (street, PLZ, Ort, Land), which the invoice prints.
 */
export const getPatientDebtorProblems = (
	patient: PatientEntityType
): FieldProblem[] => {
	const fields = new Set(
		validatePaymentSlipDebtor(patient).map((problem) => problem.field)
	);
	if (!patient.street) fields.add("street");
	if (!patient.postal_code) fields.add("postal_code");
	if (!patient.city) fields.add("city");
	if (!patient.country) fields.add("country");

	const problems: FieldProblem[] = [];
	if (fields.has("name")) {
		const message = "Der Name des Patienten fehlt oder ist ungültig.";
		problems.push(
			{ fieldId: "patients.first_name", message },
			{ fieldId: "patients.last_name", message }
		);
	}
	if (fields.has("street")) {
		problems.push({
			fieldId: "patients.street",
			message: "Die Strasse fehlt oder ist ungültig.",
		});
	}
	if (
		fields.has("postal_code") ||
		fields.has("city") ||
		fields.has("country")
	) {
		problems.push({
			fieldId: "patients.city_postal_code_country",
			message: "PLZ, Ort oder Land fehlt oder ist ungültig.",
		});
	}
	return problems;
};
